import { FolderListView } from "components/file-system/folder-view/FolderListView";
import { ReactNode, useState } from "react";
import { Dialog } from "components/dialog";
import { LibraryDialogContent } from "components/file-system/LibraryDialogContent";
import { BiLibrary } from "react-icons/bi";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { Save, RotateCcw } from "lucide-react";
import toast from "react-hot-toast";
import { useConfirmation } from "components/dialog/confirmation";
import { FileSystemItem } from "types";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation, Trans } from "react-i18next";
import { Library } from "constants/library";

const Workflows = () => {
  const { t } = useTranslation();
  const { workflows, setWorkflows, updateAgentWorkflows, originalWorkflows } =
    useAgentInfo();

  const [searchValue, setSearchValue] = useState("");
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [newWorkflows, setNewWorkflows] = useState<FileSystemItem[]>([]);
  const [removeWorkflows, setRemoveWorkflows] = useState<FileSystemItem[]>([]);

  const { confirm } = useConfirmation();

  // Reset to original state
  const reset = async () => {
    const confirmed = await confirm({
      title: t("agents.workflows.resetChanges"),
      message: t("agents.workflows.resetChangesConfirm"),
    });

    if (confirmed) {
      setWorkflows(originalWorkflows);
      setNewWorkflows([]);
      setRemoveWorkflows([]);
      toast.success(t("agents.workflows.resetSuccess"));
    }
  };

  const filteredItems = workflows.filter((f) => {
    console.log(workflows, searchValue);
    return f.name.toLowerCase().includes(searchValue.toLowerCase());
  });

  const handleSubmit = async () => {
    const addCount = newWorkflows.length;
    const removeCount = removeWorkflows.length;

    let confirmationMessage: string | ReactNode =
      t("agents.workflows.updateConfirm");
    if (addCount > 0 && removeCount > 0) {
      confirmationMessage = (
        <p>
          <Trans
            i18nKey="agents.workflows.updateConfirmWithBoth"
            values={{ addCount, removeCount }}
            components={{
              green: <span className="text-green-500" />,
              red: <span className="text-red-500" />,
            }}
          />
        </p>
      );
    } else if (addCount > 0) {
      confirmationMessage = (
        <p>
          <Trans
            i18nKey="agents.workflows.updateConfirmAdd"
            values={{ addCount }}
            components={{
              green: <span className="text-green-500" />,
            }}
          />
        </p>
      );
    } else if (removeCount > 0) {
      confirmationMessage = (
        <p>
          <Trans
            i18nKey="agents.workflows.updateConfirmRemove"
            values={{ removeCount }}
            components={{
              red: <span className="text-red-500" />,
            }}
          />
        </p>
      );
    }

    const confirmed = await confirm({
      title: t("agents.workflows.updateTitle"),
      message: confirmationMessage,
    });
    if (confirmed) {
      try {
        await updateAgentWorkflows(
          newWorkflows.map((i) => i.id),
          removeWorkflows.map((i) => i.agent_workflow_id)
        );
        setNewWorkflows([]);
        setRemoveWorkflows([]);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const handleDelete = async (removeWorkflow?: FileSystemItem) => {
    const confirmed = await confirm({
      title: t("agents.workflows.deleteFile"),
      message: t("agents.workflows.deleteFileConfirm"),
      variant: "danger",
    });

    if (confirmed) {
      if (!removeWorkflow) {
        setWorkflows(
          workflows.filter(
            (kb) => !removeWorkflows.some((doc) => doc.id === kb.id)
          )
        );
        return;
      }

      if (newWorkflows.some((doc) => doc.id === removeWorkflow.id)) {
        setNewWorkflows(
          newWorkflows.filter((doc) => doc.id !== removeWorkflow.id)
        );
      } else if (!removeWorkflows.some((doc) => doc.id === removeWorkflow.id)) {
        setRemoveWorkflows([...removeWorkflows, removeWorkflow]);
      }
      setWorkflows(workflows.filter((doc) => doc.id !== removeWorkflow.id));
    }
  };

  const handleFileClick = (removeWorkflow: FileSystemItem) => {
    const isInRemoveList = removeWorkflows.some((doc) => {
      return doc.id === removeWorkflow.id;
    });

    if (isInRemoveList) {
      setRemoveWorkflows(
        removeWorkflows.filter((doc) => doc.id !== removeWorkflow.id)
      );
    } else {
      setRemoveWorkflows([...removeWorkflows, removeWorkflow]);
    }
  };

  const handleLibraryConfirm = (items: FileSystemItem[]) => {
    const toNewWorkflowIds = items.filter(
      (i) => !originalWorkflows.some((f) => f.id === i.id)
    );

    const toRemoveWorkflowIds = originalWorkflows.filter(
      (f) => !items.some((i) => i.id === f.id)
    );

    const toNewWorkflowIdss = toNewWorkflowIds.map((i) => {
      delete i.type;
      return { ...i };
    });

    setNewWorkflows(toNewWorkflowIdss);
    setRemoveWorkflows(toRemoveWorkflowIds);
    setWorkflows(items);
  };

  const hasChanges = newWorkflows.length > 0 || removeWorkflows.length > 0;

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <div>
            <SearchAndNew
              className="!mx-0 mb-3 md:mb-0"
              value={searchValue}
              onChange={(s) => setSearchValue(s)}
              showAdd={false}
            />
          </div>
        </div>
        <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-auto md:flex-row">
          <Dialog
            header={t("agents.workflows.selectNewWorkflows")}
            subHeader={t("agents.workflows.updateAgentWorkflows")}
            trigger={
              <button
                type="button"
                onClick={() => setIsLibraryOpen(true)}
                className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 md:w-auto"
              >
                <BiLibrary className="text-lg" />
                <span>{t("agents.workflows.updateFromLibrary")}</span>
              </button>
            }
            isOpen={isLibraryOpen}
            setIsOpen={setIsLibraryOpen}
            children={
              <LibraryDialogContent
                selectedItems={workflows}
                filterItem={(items) => {
                  return items.filter(
                    (item) =>
                      !originalWorkflows.some(
                        (originalItem) => originalItem.id === item.id
                      )
                  );
                }}
                handleConfirm={handleLibraryConfirm}
                setIsLibraryOpen={setIsLibraryOpen}
                rootFolderName={Library.WORKFLOWS}
              />
            }
          />
          <div className="flex gap-2">
            <button
              disabled={!hasChanges}
              onClick={handleSubmit}
              className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
            >
              <Save size={15} />
              <span>{t("common.save")}</span>
            </button>
            <button
              disabled={!hasChanges}
              onClick={reset}
              title={t("agents.workflows.resetToOriginal")}
              className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-auto">
        <FolderListView
          selectedFiles={removeWorkflows}
          setSelectedFiles={setRemoveWorkflows}
          handleDelete={handleDelete}
          onFileClick={handleFileClick}
          willShowCheckbox={(item) => {
            return false;
          }}
          willUpdateUrl={false}
          fileItems={filteredItems}
        />
      </div>
    </>
  );
};

export default Workflows;
